import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { products, categories } from '../../../mocks/products';
import { useCart } from '../../../context/CartContext';
import { useSEO } from '@/hooks/useSEO';
import { SocialSidebar } from '../../../components/feature/SocialSidebar';
import { ShareButtons } from '../components/ShareButtons';

// ─── SEO wrapper — renders nothing, just fires useSEO ─────────────────────────
interface ProductSEOProps {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  rating: number;
  reviews: number;
  inStock: boolean;
}

const ProductSEO = ({ id, name, description, price, image, rating, reviews, inStock }: ProductSEOProps) => {
  const canonical = `https://www.zpiritualz.com/shop/${id}`;

  useSEO({
    title: `${name} | ZpiritualZ – Spirituele Beschermingsproducten`,
    description: `${description.slice(0, 155)}`,
    canonical,
    ogImage: image,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name,
      description,
      image,
      url: canonical,
      brand: {
        '@type': 'Brand',
        name: 'ZpiritualZ',
      },
      offers: {
        '@type': 'Offer',
        price: price.toFixed(2),
        priceCurrency: 'EUR',
        availability: inStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        url: canonical,
        seller: {
          '@type': 'Organization',
          name: 'ZpiritualZ',
        },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: rating,
        reviewCount: reviews,
        bestRating: 5,
        worstRating: 1,
      },
    },
  });

  return null;
};

// ─── Main page ─────────────────────────────────────────────────────────────────
const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart, openCart } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<'beschrijving' | 'gebruik' | 'reviews'>('beschrijving');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setQty(1);
    setAdded(false);
    setActiveTab('beschrijving');
  }, [id]);

  const product = products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#050010] flex flex-col items-center justify-center pt-20">
        <i className="ri-ghost-line text-6xl text-white/15 mb-4 block" />
        <p className="text-white/40 text-lg mb-6">Product niet gevonden</p>
        <Link
          to="/shop"
          className="px-6 py-3 bg-violet-600/20 text-violet-300 border border-violet-500/30 rounded-xl text-sm font-medium hover:bg-violet-600/30 transition-colors whitespace-nowrap cursor-pointer"
        >
          Terug naar shop
        </Link>
      </div>
    );
  }

  const category = categories.find(c => c.id === product.category);
  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 3);
  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  const handleAdd = () => {
    const itemType = product.category === 'evil-energy-destroy-kit' ? 'kit' : 'product';
    for (let i = 0; i < qty; i++) {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        type: itemType,
        image: product.image,
        description: product.description,
      });
    }
    setAdded(true);
    setTimeout(() => {
      setAdded(false);
      openCart();
    }, 800);
  };

  return (
    <div className="min-h-screen bg-[#050010] pt-20">
      <ProductSEO
        id={product.id}
        name={product.name}
        description={product.description}
        price={product.price}
        image={product.image}
        rating={product.rating}
        reviews={product.reviews}
        inStock={product.inStock}
      />
      <SocialSidebar />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-white/30 mb-8">
          <Link to="/" className="hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/shop" className="hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap">Shop</Link>
          <i className="ri-arrow-right-s-line" />
          <button
            onClick={() => navigate(`/shop?cat=${product.category}`)}
            className="hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
          >
            {category?.label}
          </button>
          <i className="ri-arrow-right-s-line" />
          <span className="text-white/55 truncate max-w-[200px]">{product.name}</span>
        </nav>

        {/* Main Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden bg-[#110020]/60 border border-violet-500/15 aspect-square">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
              {product.badge && (
                <span className="absolute top-5 left-5 px-4 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-full">
                  {product.badge}
                </span>
              )}
              {discount && (
                <span className="absolute top-5 right-5 px-3 py-1.5 bg-pink-600/80 text-white text-xs font-bold rounded-xl">
                  -{discount}%
                </span>
              )}
              {product.category === 'blue-protect' && (
                <div className="absolute bottom-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-violet-600/60 backdrop-blur-sm border border-violet-400/30">
                  <i className="ri-shield-star-line text-white text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            {/* Category */}
            <span className="text-violet-400/70 text-xs uppercase tracking-widest mb-3">
              {category?.label}
            </span>

            {/* Title */}
            <h1 className="text-3xl font-black text-white leading-tight mb-4">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`ri-star-fill text-sm ${i < Math.floor(product.rating) ? 'text-[#d4a017]' : 'text-white/15'}`}
                  />
                ))}
              </div>
              <span className="text-[#d4a017] font-semibold text-sm">{product.rating}</span>
              <span className="text-white/35 text-xs">({product.reviews} beoordelingen)</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-4xl font-black text-white">&euro;{product.price.toFixed(2)}</span>
              {product.originalPrice && (
                <>
                  <span className="text-white/25 text-xl line-through">&euro;{product.originalPrice.toFixed(2)}</span>
                  <span className="px-2 py-0.5 bg-pink-600/20 text-pink-400 text-xs font-bold rounded-lg border border-pink-500/20">
                    -{discount}% korting
                  </span>
                </>
              )}
            </div>

            {/* Short description */}
            <p className="text-white/50 text-sm leading-relaxed mb-8 border-l-2 border-violet-500/30 pl-4">
              {product.description}
            </p>

            {/* In Stock */}
            <div className="flex items-center gap-2 mb-6">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-emerald-400 text-xs font-medium">Op voorraad — direct leverbaar</span>
            </div>

            {/* Quantity + Add to cart */}
            <div className="flex items-center gap-4 mb-6">
              {/* Qty selector */}
              <div className="flex items-center gap-0 bg-[#110020]/80 border border-violet-500/20 rounded-xl overflow-hidden">
                <button
                  onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-white hover:bg-violet-600/15 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-sm" />
                </button>
                <span className="w-10 text-center text-white font-semibold text-sm">{qty}</span>
                <button
                  onClick={() => setQty(q => q + 1)}
                  className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-white hover:bg-violet-600/15 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-sm" />
                </button>
              </div>

              {/* Add to cart — primaire knop */}
              <button
                onClick={handleAdd}
                className={`flex-1 flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  added
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-90'
                }`}
              >
                {added ? (
                  <><i className="ri-check-line text-base" /> Toegevoegd aan wagen!</>
                ) : (
                  <><i className="ri-shopping-bag-line text-base" /> In Wagen — &euro;{(product.price * qty).toFixed(2)}</>
                )}
              </button>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-3 pt-6 border-t border-violet-500/10">
              {[
                { icon: 'ri-truck-line', label: 'Gratis verzending', sub: 'Boven €50' },
                { icon: 'ri-shield-check-line', label: 'Veilig betalen', sub: 'SSL beveiligd' },
                { icon: 'ri-refresh-line', label: '30 dagen retour', sub: 'Geen gedoe' },
              ].map(b => (
                <div key={b.icon} className="flex flex-col items-center text-center gap-1">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${b.icon} text-violet-400 text-lg`} />
                  </div>
                  <span className="text-white text-xs font-medium">{b.label}</span>
                  <span className="text-white/30 text-xs">{b.sub}</span>
                </div>
              ))}
            </div>

            {/* Share buttons */}
            <div className="pt-5 border-t border-violet-500/10">
              <ShareButtons
                productName={product.name}
                productId={product.id}
                price={product.price}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-12">
          {/* Tab headers */}
          <div className="flex gap-1 p-1 bg-[#110020]/60 border border-violet-500/15 rounded-xl w-fit mb-6">
            {(['beschrijving', 'gebruik', 'reviews'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap capitalize ${
                  activeTab === tab
                    ? 'bg-violet-600/30 text-violet-300 border border-violet-500/30'
                    : 'text-white/40 hover:text-white/70'
                }`}
              >
                {tab === 'beschrijving' ? 'Beschrijving' : tab === 'gebruik' ? 'Gebruik & Ingrediënten' : `Reviews (${product.reviewList?.length ?? 0})`}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="bg-[#110020]/40 border border-violet-500/10 rounded-2xl p-6 lg:p-8">
            {activeTab === 'beschrijving' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-white font-bold text-base mb-4">Over dit product</h3>
                  <p className="text-white/55 text-sm leading-relaxed mb-6">
                    {product.fullDescription || product.description}
                  </p>
                </div>
                {product.benefits && (
                  <div>
                    <h3 className="text-white font-bold text-base mb-4">Voordelen</h3>
                    <ul className="space-y-2.5">
                      {product.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-line text-violet-400 text-base" />
                          </div>
                          <span className="text-white/60 text-sm">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'gebruik' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {product.howToUse && (
                  <div>
                    <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-book-open-line text-violet-400" />
                      </div>
                      Gebruiksaanwijzing
                    </h3>
                    <p className="text-white/55 text-sm leading-relaxed">{product.howToUse}</p>
                  </div>
                )}
                {product.ingredients && (
                  <div>
                    <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-leaf-line text-violet-400" />
                      </div>
                      Ingrediënten / Specificaties
                    </h3>
                    <p className="text-white/55 text-sm leading-relaxed">{product.ingredients}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                {/* Average */}
                <div className="flex items-center gap-6 mb-8 p-5 bg-[#110020]/60 rounded-xl border border-violet-500/10">
                  <div className="text-center">
                    <p className="text-5xl font-black text-white">{product.rating}</p>
                    <div className="flex items-center justify-center gap-0.5 mt-1 mb-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`ri-star-fill text-sm ${i < Math.floor(product.rating) ? 'text-[#d4a017]' : 'text-white/15'}`} />
                      ))}
                    </div>
                    <p className="text-white/30 text-xs">{product.reviews} beoordelingen</p>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {[5, 4, 3, 2, 1].map(star => {
                      const pct = star === 5 ? 78 : star === 4 ? 16 : star === 3 ? 4 : star === 2 ? 1 : 1;
                      return (
                        <div key={star} className="flex items-center gap-2.5">
                          <span className="text-white/35 text-xs w-2">{star}</span>
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-[#d4a017] text-xs" />
                          </div>
                          <div className="flex-1 h-1.5 bg-white/8 rounded-full overflow-hidden">
                            <div className="h-full bg-[#d4a017] rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-white/30 text-xs w-6">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Review cards */}
                <div className="space-y-4">
                  {product.reviewList?.map((rev, i) => (
                    <div key={i} className="p-4 bg-[#110020]/50 rounded-xl border border-violet-500/10">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white text-xs font-bold">
                            {rev.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-white text-sm font-semibold">{rev.name}</p>
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }).map((_, j) => (
                                <i key={j} className={`ri-star-fill text-xs ${j < rev.rating ? 'text-[#d4a017]' : 'text-white/15'}`} />
                              ))}
                            </div>
                          </div>
                        </div>
                        <span className="text-white/25 text-xs">{rev.date}</span>
                      </div>
                      <p className="text-white/55 text-sm leading-relaxed">{rev.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Blue Protect Series Banner — only for blue-protect products */}
        {product.category === 'blue-protect' && (
          <div className="mb-12 relative rounded-2xl overflow-hidden border border-violet-500/20">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/6ae0c7f8ed658b12f0a55eef44f10ab9.jpg"
              alt="Blue Protect Serie"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050010]/95 via-[#050010]/85 to-[#050010]/60" />
          </div>
          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 p-8 lg:p-10">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-violet-600/20 border border-violet-500/30 shrink-0">
                <i className="ri-shield-star-line text-violet-400 text-2xl" />
              </div>
              <div>
                <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-1.5">
                  ✦ Onderdeel van de serie ✦
                </p>
                <h3 className="text-white font-bold text-xl mb-2">Blue Protect Serie</h3>
                <p className="text-white/55 text-sm leading-relaxed max-w-xl">
                  <strong className="text-violet-300">{product.name}</strong> maakt deel uit van de exclusieve{' '}
                  <strong className="text-white">Blue Protect Serie</strong> — een collectie beschermingsproducten
                  gebaseerd op eeuwenoude tradities rondom blauw poeder (<em>Ogri Ai</em>, <em>Nazar</em>,{' '}
                  <em>Azul</em>). Elk product is samengesteld om jou en je omgeving te beschermen tegen
                  negatieve energieën en het boze oog.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <Link
                to="/shop#blue-protect-info"
                className="flex items-center gap-2 px-6 py-3 bg-violet-600/20 hover:bg-violet-600/35 border border-violet-500/30 hover:border-violet-500/50 text-violet-300 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line" />
                Meer over Blue Protect
              </Link>
              <Link
                to="/shop?cat=blue-protect"
                className="flex items-center gap-2 px-6 py-3 bg-white/4 hover:bg-white/8 border border-white/10 hover:border-white/20 text-white/60 hover:text-white rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-store-2-line" />
                Alle Blue Protect producten
              </Link>
            </div>
          </div>
          {/* Decorative shield icons */}
          <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-5 hidden lg:block">
            <i className="ri-shield-star-fill text-white" style={{ fontSize: '120px' }} />
          </div>
        </div>
        )}

        {/* Related Products */}
        {related.length > 0 && (
          <div>
            <h2 className="text-white font-bold text-xl mb-6">Gerelateerde Producten</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map(rel => (
                <Link
                  key={rel.id}
                  to={`/shop/${rel.id}`}
                  className="group bg-[#110020]/70 border border-violet-500/12 hover:border-violet-500/35 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-violet-400/60 text-xs uppercase tracking-wide mb-1">
                      {categories.find(c => c.id === rel.category)?.label}
                    </p>
                    <h3 className="text-white font-semibold text-sm mb-2">{rel.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold">&euro;{rel.price.toFixed(2)}</span>
                      <span className="text-violet-400 text-xs hover:text-violet-300 transition-colors whitespace-nowrap">
                        Bekijken &rarr;
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
